import { ResponseWithPagination, SortOptions } from '~src/types/api';
import { apiCweb } from './api-cweb';
import { DbAnswer, DbQuestion, DbQuestionChangeLogItem } from '~src/types/db';
import { appLog } from '~src/utils/log';
import { SharedTenderParams } from './shares';
import { QuestionFormValues } from '~components/atoms/Questions/QuestionForm';

type BaseGetQuestionsListParams = {
  projectId?: number;
  tenderId: string;
};

export type GetQuestionsListParams = BaseGetQuestionsListParams &
  SortOptions & { showRejected?: boolean };

export const getQuestionsList = async (
  key,
  { projectId, tenderId, skip, take, showRejected }: GetQuestionsListParams
) => {
  appLog('getQuestionsList ->', { projectId });
  const res = await apiCweb.get<DbQuestion[]>(
    `/projects/${projectId}/tenders/${tenderId}/questions`,
    {
      params: {
        show_rejected: showRejected,
        skip,
        take,
        sort: 'desc(created_at)',
      },
    }
  );
  return { questions: res.data, totalCount: res.headers['total-count'] };
};

export type GetQuestionsChangeLogListParams = BaseGetQuestionsListParams & {
  questionId: number;
};

export const getQuestionsChangeLogList = async ({
  projectId,
  tenderId,
  questionId,
}: GetQuestionsChangeLogListParams) => {
  appLog('getQuestionsChangeLogList ->', { projectId, tenderId, questionId });
  const res = await apiCweb.get<DbQuestionChangeLogItem[]>(
    `/projects/${projectId}/tenders/${tenderId}/questions/${questionId}/change_logs`
  );
  return res.data;
};

export const getAnswersList = async (
  key,
  { projectId, tenderId, skip, take }: BaseGetQuestionsListParams & SortOptions
) => {
  appLog('getAnswersList ->', { projectId });
  const res = await apiCweb.get<DbQuestion[]>(
    `/projects/${projectId}/tenders/${tenderId}/answers`,
    { params: { skip, take } }
  );
  return res.data;
};

export const getSharedTenderQuestionsList = async ({
  share_id,
  share_key,
  skip,
  take,
}: SharedTenderParams & SortOptions): Promise<
  ResponseWithPagination<DbQuestion>
> => {
  appLog('getSharedTenderQuestionsList ->', { share_id, share_key });
  const res = await apiCweb.get<DbQuestion[]>(
    `/tender_shares/${share_id}/questions`,
    {
      headers: { 'Share-Key': share_key },
      params: { skip, take, sort: 'desc(created_at)' },
    }
  );
  return { items: res.data, totalCount: res.headers['total-count'] };
};

export const getSharedTenderMyQuestionsList = async ({
  share_id,
  share_key,
  skip,
  take,
}: SharedTenderParams & SortOptions): Promise<
  ResponseWithPagination<DbQuestion>
> => {
  appLog('getSharedTenderQuestionsList ->', { share_id, share_key });
  const res = await apiCweb.get<DbQuestion[]>(
    `/tender_shares/${share_id}/my_questions`,
    {
      headers: { 'Share-Key': share_key },
      params: { skip, take, sort: 'desc(created_at)' },
    }
  );
  return { items: res.data, totalCount: res.headers['total-count'] };
};

type AddAnswerToQuestionParams = {
  questionId: number;
  data: { answer: string };
  sendNotification?: boolean;
} & BaseGetQuestionsListParams;

export const addAnswerToQuestion = async ({
  projectId,
  tenderId,
  questionId,
  data,
  sendNotification,
}: AddAnswerToQuestionParams) => {
  appLog('addAnswerToQuestion ->', { projectId, sendNotification });
  const res = await apiCweb.post<DbAnswer>(
    `/projects/${projectId}/tenders/${tenderId}/questions/${questionId}/answers`,
    data,
    {
      params:
        sendNotification !== undefined
          ? { send_notification: sendNotification }
          : undefined,
    }
  );
  return res.data;
};

type ChangeAnswerStateParams = {
  questionId: number;
  answerId: number;
  action: 'remove' | 'reject' | 'restore';
} & BaseGetQuestionsListParams;

export const changeAnswerState = async ({
  projectId,
  tenderId,
  questionId,
  answerId,
  action,
}: ChangeAnswerStateParams) => {
  appLog('changeQuestionState ->', { projectId });
  const res = await apiCweb.put(
    `/projects/${projectId}/tenders/${tenderId}/questions/${questionId}/answers/${answerId}`,
    {},
    { params: { action } }
  );
  return res.data;
};

type ChangeQuestionStateParams = {
  questionId: number;
  action: 'accept' | 'reject';
} & BaseGetQuestionsListParams;

export const changeQuestionState = async ({
  projectId,
  tenderId,
  questionId,
  action,
}: ChangeQuestionStateParams) => {
  appLog('changeQuestionState ->', { projectId });
  const res = await apiCweb.put<DbQuestion>(
    `/projects/${projectId}/tenders/${tenderId}/questions/${questionId}`,
    {},
    { params: { action } }
  );
  return res.data;
};

type AddQuestionParams = {
  data: QuestionFormValues;
} & SharedTenderParams;

export const addQuestion = async ({
  share_id,
  share_key,
  data,
}: AddQuestionParams) => {
  appLog('addQuestion ->', { share_id, share_key });
  const res = await apiCweb.post<DbQuestion>(
    `/tender_shares/${share_id}/questions`,
    { question: data.question },
    { headers: { 'Share-Key': share_key } }
  );
  return res.data;
};

type UpdateQuestionParams = {
  data: QuestionFormValues;
  questionId: number;
} & BaseGetQuestionsListParams;

export const updateQuestion = async ({
  projectId,
  tenderId,
  questionId,
  data,
}: UpdateQuestionParams) => {
  appLog('updateQuestion ->', { projectId, tenderId, questionId, data });
  const res = await apiCweb.put<DbQuestion>(
    `/projects/${projectId}/tenders/${tenderId}/questions/${questionId}?action=update`,
    { question: data.question }
  );
  return res.data;
};

// Get questions (optionally by ids)
type getQuestionsDataParams = {
  project_id: number;
  tender_id: number | string;
  question_ids?: string; // comma-separated ids
  sort?: string;
  skip?: number;
  take?: number;
  showRejected?: boolean;
};

/**
 * Wrapper for GET /projects/{project_id}/tenders/{tender_id}/questions.
 * Backend supports optional `question_ids` query param (comma-separated ids)
 * to fetch specific questions.
 */
export const getQuestionsData = async ({
  project_id,
  tender_id,
  question_ids,
  sort,
  skip,
  take,
  showRejected,
}: getQuestionsDataParams): Promise<DbQuestion[]> => {
  appLog('getQuestionsData #1 -> ', {
    project_id,
    tender_id,
    question_ids,
    sort,
    skip,
    take,
    showRejected,
  });

  const res = await apiCweb.get<DbQuestion[]>(
    `/projects/${project_id}/tenders/${tender_id}/questions`,
    {
      params: {
        question_ids,
        show_rejected: showRejected,
        sort,
        skip,
        take,
      },
    }
  );

  appLog(`getQuestionsData #2 -> `, res.data);
  return res.data;
};

export const getQuestionsDataVendors = async ({
  shareId,
  shareKey,
  question_ids,
}: {
  shareId: string;
  shareKey: string;
  question_ids?: string;
}) => {
  appLog('getQuestionsDataVendors ->', { shareId, question_ids });
  const res = await apiCweb.get<DbQuestion[]>(
    `/tender_shares/${shareId}/questions`,
    {
      headers: { 'Share-Key': shareKey },
      params: { question_ids },
    }
  );
  return res.data;
};

type GetAnswerParams = {
  questionId: number;
  answerId: number;
} & BaseGetQuestionsListParams;

export const getAnswer = async ({
  projectId,
  tenderId,
  questionId,
  answerId,
}: GetAnswerParams) => {
  appLog('getAnswer ->', { projectId, answerId });
  const res = await apiCweb.get<DbAnswer>(
    `/projects/${projectId}/tenders/${tenderId}/questions/${questionId}/answers/${answerId}`,
    {
      params: { include_removed_answers: true },
    }
  );
  return res.data;
};

export const getPendingQuestionsCount = async ({
  projectId,
  tenderId,
}: {
  projectId: number;
  tenderId: string;
}): Promise<number> => {
  const res = await apiCweb.get<{ pending_count: number }>(
    `/projects/${projectId}/tenders/${tenderId}/questions/pending_count`
  );
  return res.data.pending_count;
};

export type ExportQuestionsParams = BaseGetQuestionsListParams & {
  responseType: 'csv' | 'csv_inline';
};

export const exportQuestionsCsv = async ({
  projectId,
  tenderId,
  responseType,
}: ExportQuestionsParams) => {
  appLog('exportQuestionsCsv ->', { projectId, tenderId, responseType });
  const res = await apiCweb.get(
    `/projects/${projectId}/tenders/${tenderId}/questions`,
    {
      params: { response_type: responseType },
      responseType: 'blob',
    }
  );
  return res;
};
